import React, { useState } from 'react';
import { Minus, Maximize2, X, ChevronRight, ChevronLeft } from 'lucide-react';
import AgentChat from './AgentChat';
import AgentSessionList from './AgentSessionList';
import { useAgent } from '../../context/AgentContext';

/**
 * AgentPanel — default expanded panel (420px, center chat only).
 *
 * Props:
 *   pageContext
 *   onMinimize
 *   onFullScreen
 *   onKillSession
 */
const AgentPanel = ({ pageContext, onMinimize, onFullScreen, onKillSession }) => {
  const { activeSession, createSession } = useAgent();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleNewSession = async () => {
    await createSession(pageContext);
    setIsDrawerOpen(false);
  };

  return (
    <div
      className="fixed bottom-20 right-6 z-[9999] bg-accent agent-dot-bg rounded-lg shadow-2xl border border-white/20 flex flex-col overflow-hidden"
      style={{ width: '420px', height: '640px', maxHeight: 'calc(100vh - 120px)' }}
    >
      {/* Sessions drawer — slides over the chat from the left */}
      {isDrawerOpen && (
        <div className="absolute inset-y-0 left-0 w-60 bg-accent border-r border-white/20 z-10 flex flex-col">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/20 flex-shrink-0">
            <span className="text-xs font-medium text-white/80">Sessions</span>
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded transition-colors"
              title="Close sessions"
            >
              <ChevronLeft size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <AgentSessionList
              onNewSession={handleNewSession}
              onSessionSelect={() => setIsDrawerOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <VasariLogo />
          <span className="text-xs font-medium text-accent truncate">
            {activeSession?.title || 'Vasari Agent'}
          </span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => setIsDrawerOpen((v) => !v)}
            className="flex items-center gap-0.5 px-1.5 py-1 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors mr-1"
            title="Sessions"
          >
            <span className="text-xs leading-none">Sessions</span>
            <ChevronRight size={12} />
          </button>
          <button
            onClick={onMinimize}
            className="p-1.5 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors"
            title="Minimize"
          >
            <Minus size={14} />
          </button>
          <button
            onClick={onFullScreen}
            className="p-1.5 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors"
            title="Full screen"
          >
            <Maximize2 size={14} />
          </button>
          <button
            onClick={onKillSession}
            className="p-1.5 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors"
            title="Kill session"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 overflow-hidden">
        <AgentChat pageContext={pageContext} />
      </div>
    </div>
  );
};

/* SVG logo mark — blue on white variant for panel header */
const VasariLogo = () => (
  <svg width="16" height="16" viewBox="0 0 8689 9614" fill="#1D4ED8" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
    <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
    <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
    <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
    <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
    <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
    <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
    <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
    <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
    <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
    <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
    <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
    <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
    <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
    <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
    <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
    <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
    <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
  </svg>
);

export default AgentPanel;
